import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface SubscriptionOptions {
  table: string;
  schema?: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  userId?: string;
  queryKeys?: readonly unknown[][];
  onRecordChange?: (payload: any) => void;
}

/**
 * A hook to manage Supabase real-time subscriptions
 * 
 * @param options The subscription options
 * @returns An object with the subscription channel
 */
export function useSupabaseSubscription(options: SubscriptionOptions) {
  const {
    table,
    schema = 'public',
    event = '*',
    filter,
    userId,
    queryKeys = [],
    onRecordChange,
  } = options;
  
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Safety check to prevent errors
    if (!userId || !table) {
      // Skip silently for undefined userId - this is a valid case during initialization
      return;
    }
    
    try {
      // Cleanup any existing subscription before creating a new one
      if (subscriptionRef.current) {
        console.log(`Cleaning up previous subscription for ${table}`);
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      
      // Create a unique channel name
      const channelName = `${table}_changes_${userId}_${Date.now()}`;
      console.log(`Setting up subscription for ${table} on channel ${channelName}`);
      
      // Create subscription channel
      let channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes' as any,
          {
            event: event,
            schema: schema,
            table: table,
            filter: filter,
          },
          (payload: any) => {
            try {
              console.log(`Received real-time update for ${table}:`, payload);
              
              // Safely invalidate queries
              if (Array.isArray(queryKeys) && queryKeys.length > 0) {
                queryKeys.forEach(queryKey => {
                  if (Array.isArray(queryKey)) {
                    queryClient.invalidateQueries({ queryKey });
                  }
                });
              }
              
              // Call the custom handler if provided
              if (onRecordChange && typeof onRecordChange === 'function') {
                onRecordChange(payload);
              }
            } catch (error) {
              console.error(`Error handling real-time update for ${table}:`, error);
            }
          }
        )
        .subscribe((status) => {
          console.log(`Subscription status for ${table}:`, status);
        });
      
      subscriptionRef.current = channel;
    } catch (error) {
      console.error(`Error setting up subscription for ${table}:`, error);
    }
    
    // Cleanup subscription on unmount
    return () => {
      try {
        if (subscriptionRef.current) {
          console.log(`Cleaning up subscription for ${table}`);
          supabase.removeChannel(subscriptionRef.current);
          subscriptionRef.current = null;
        }
      } catch (error) {
        console.error(`Error cleaning up subscription for ${table}:`, error);
      }
    };
  }, [table, schema, event, filter, userId, queryClient]);
  
  return { subscription: subscriptionRef.current };
}